import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { DataView } from 'primereact/dataview';
import { Tag } from 'primereact/tag';

import { useMutation } from 'react-query';
import { downloadScientificProfileFile, getById } from 'apis/employees.api';
import { getAcadamicLevelShortHand } from 'utils/func';

export default function ViewDetail({ selectedEmployee, visible, setVisible, statuses }) {
  // #region Data
  const { t } = useTranslation();

  const [employee, setEmployee] = useState({});

  useEffect(() => {
    if (visible) {
      (async () => {
        const { data } = await getById(selectedEmployee?.id);
        setEmployee(data);
      })();
    }

    return () => {
      setEmployee({});
    };
  }, [visible]);

  const { mutate: mutateDownloadScientificProfileFile } = useMutation(
    downloadScientificProfileFile
  );

  // eslint-disable-next-line no-unused-vars
  const handleDownloadScientificProfileFile = () => {
    mutateDownloadScientificProfileFile(
      { userId: employee?.userId },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = `Ly lich khoa hoc - ${getAcadamicLevelShortHand(
              employee?.scientificProfile?.academicRank,
              employee?.scientificProfile?.academicDegree,
              t,
              true
            )}${employee?.fullname}.pdf`;

            link.click();
          }
        },
      }
    );
  };

  const handleDisplayScientificProfileFile = () => {
    mutateDownloadScientificProfileFile(
      { userId: employee?.userId },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data], { type: 'application/pdf' });
            const blobURL = URL.createObjectURL(file);
            window.open(blobURL);
          }
        },
      }
    );
  };

  const organizationName = useMemo(() => {
    if (employee?.organization?.englishName === 'Other') {
      return employee?.specificOrganizationName;
    }
    return t('lang') === 'vi' ? employee?.organization?.name : employee?.organization?.englishName;
  }, [employee, t]);
  // #endregion

  const formatStatus = (value) => {
    if (!value?.user?.status) return '';

    const statusColorMapping = {
      inactive: 'warning',
      active: 'success',
      blocked: 'danger',
      pending: 'info',
    };

    return (
      <Tag
        className="w-7rem"
        severity={statusColorMapping[value.user.status]}
        value={statuses[value.user.status]}
        rounded
      />
    );
  };

  const formatResearchField = (researchField) => (
    <div style={{ margin: '2px' }}>
      <span className="p-tag p-component p-tag-rounded bg-cyan-600">
        <span className="p-tag-value">
          {t(`researchFields.${researchField?.researchField?.code}`)}
        </span>
      </span>
    </div>
  );

  return (
    <Dialog
      header={<p className="text-blue-500">{t('employee.view.title')}</p>}
      headerStyle={{ padding: '1.5rem' }}
      visible={visible}
      draggable={false}
      style={{ width: '500px' }}
      onHide={() => setVisible(false)}
    >
      <div className="flex flex-wrap gap-4">
        <div className="flex flex-column gap-3 mb-2">
          <span className="text-lg md:text-xl xl:text-2xl font-semibold">
            {`${getAcadamicLevelShortHand(
              employee?.scientificProfile?.academicRank,
              employee?.scientificProfile?.academicDegree,
              t,
              true
            )}${employee?.fullname}`}
          </span>
          <div className="flex align-item-center gap-4">
            <span className="">{formatStatus(employee)}</span>
            <span className="text-gray-600 font-semibold" style={{ marginTop: '2px' }}>
              <i className="pi pi-tag pr-1" />
              {employee?.user?.userRoles
                ?.slice()
                ?.sort((a, b) => a.role.code.localeCompare(b.role.code))
                ?.map((item) => t(`userRole.${item?.role?.code}`))
                ?.join(', ')}
            </span>
          </div>
        </div>
        <div>
          <div className="flex">
            <span className="w-6rem lg:w-8rem font-semibold mr-2">Email:</span>
            {employee?.email && (
              <a
                href={`mailto:${employee.email}`}
                className="max-w-20rem font-medium text-blue-600"
                target="_blank"
                rel="noreferrer"
              >
                {employee?.email.split('@')[0]}
                <wbr />@{employee?.email.split('@')[1]}
              </a>
            )}
          </div>
          <div className="flex mt-3">
            <span className="w-6rem lg:w-8rem font-semibold mr-2">{t('employee.phone')}:</span>
            {employee?.scientificProfile?.personalPhone && (
              <a
                href={`tel:${employee?.scientificProfile?.personalPhone}`}
                className="max-w-20rem font-medium text-blue-600"
              >
                {employee?.scientificProfile?.personalPhone}
              </a>
            )}
          </div>
          <div className="flex mt-3">
            <span className="w-6rem lg:w-8rem font-semibold mr-2">
              {t('employee.organization')}:
            </span>
            <p className="max-w-20rem font-medium">{organizationName}</p>
          </div>
          <div className="flex mt-3">
            <span className="w-6rem lg:w-8rem font-semibold mr-2">{t('employee.gender')}:</span>
            <p className="max-w-20rem font-medium">
              {employee?.scientificProfile?.gender
                ? t(`userProfile.gender.${employee?.scientificProfile?.gender}`)
                : ''}
            </p>
          </div>
          <div className="flex mt-3">
            <span className="w-6rem lg:w-8rem font-semibold mr-2">
              {t('employee.researchFields')}:
            </span>
            <DataView
              className="max-w-20rem"
              value={employee?.user?.userResearchFields}
              layout="grid"
              itemTemplate={formatResearchField}
              emptyMessage=" "
            />
          </div>
          <div className="flex mt-3">
            <span className="w-6rem lg:w-8rem font-semibold mr-2">
              {t('employee.scientificProfileFile')}:
            </span>
            {employee?.user?.scientificProfileUrl && (
              <span
                className="p-button p-button-text p-button-sm p-0 font-medium text-blue-600"
                // onClick={handleDownloadScientificProfileFile}
                onClick={handleDisplayScientificProfileFile}
              >
                <i className="pi pi-paperclip" />
                <span className="underline font-italic text-base ml-1">
                  {t('scientificProfile.downloadFile')}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

ViewDetail.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types, react/require-default-props
  selectedEmployee: PropTypes.object,
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  statuses: PropTypes.object.isRequired,
};
